import { useEffect, useState } from 'react';
import type {
  EventItem,
  FundingScenario,
  FundingSource,
  FundingSourceType,
} from '../../types';
import { computeFunding } from '../../engine/funding';
import { useStore } from '../../state/store';
import { uuid } from '../../utils/uuid';
import { MoneyInput } from '../MoneyInput';
import { PctInput } from '../PctInput';
import {
  Banner,
  Button,
  Card,
  Field,
  Icon,
  Input,
  Label,
  Money,
  SegmentedControl,
  Select,
  Textarea,
  Toggle,
} from '../../ui';
import { EVENT_META } from '../../events/meta';

interface Props {
  event: EventItem;
  onChange: (next: EventItem) => void;
  onDelete: () => void;
  onClose: () => void;
  /** Autosave the edit in place rather than requiring a Save button. */
  autoSave?: boolean;
}

/**
 * Unified editor for any EventItem. Dispatches per event kind to the right
 * field set, plus a shared header (name, year, enabled toggle) and footer
 * (delete, close).
 */
export function EventEditor({ event, onChange, onDelete, onClose, autoSave = true }: Props) {
  const { assumptions } = useStore();
  const [draft, setDraft] = useState<EventItem>(event);
  // Last saved snapshot is STATE, not a ref. Refs don't trigger re-renders
  // when mutated, so the dirty calculation can get stuck on a stale value
  // (the cause of the "stuck saving" bug — autosave wrote to the ref but
  // React didn't re-render, so dirty stayed true forever).
  const [lastSavedJson, setLastSavedJson] = useState(() => JSON.stringify(event));
  const draftJson = JSON.stringify(draft);
  const dirty = draftJson !== lastSavedJson;

  // Autosave with debounce so the projection chart updates as the user types.
  // We snapshot `draftJson` synchronously inside the timeout so a follow-up
  // edit during the debounce window doesn't race with the save.
  useEffect(() => {
    if (!autoSave || !dirty) return;
    const handle = setTimeout(() => {
      setLastSavedJson(draftJson);
      onChange(draft);
    }, 350);
    return () => clearTimeout(handle);
  }, [draft, draftJson, autoSave, dirty, onChange]);

  // Incorporate external edits (e.g., parent recomputed event after save).
  // If the parent's event is structurally identical to what we just saved,
  // no-op — only re-sync the draft on a real outside change.
  useEffect(() => {
    const eventJson = JSON.stringify(event);
    if (eventJson !== lastSavedJson) {
      setDraft(event);
      setLastSavedJson(eventJson);
    }
  }, [event, lastSavedJson]);

  const meta = EVENT_META[draft.kind];
  const calendarYear = assumptions.startingCalendarYear + draft.yearOffset;
  const ageAtEvent = assumptions.currentAge + draft.yearOffset;

  function patch(p: Partial<EventItem>) {
    setDraft(d => ({ ...d, ...p } as EventItem));
  }

  return (
    <div className="space-y-3">
      {/* Shared header */}
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg"
          style={{ backgroundColor: `${tintFor(meta.tone)}22` }}
          aria-hidden
        >
          <Icon name={meta.icon} size={20} style={{ color: tintFor(meta.tone) }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
            {meta.label}
          </div>
          <Input
            value={('name' in draft ? draft.name : '') ?? ''}
            placeholder={meta.label}
            onChange={e => patch({ name: e.target.value } as Partial<EventItem>)}
            className="mt-0.5 px-2 py-1 text-base font-semibold"
            aria-label="Event name"
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Toggle
            checked={draft.enabled}
            onChange={v => patch({ enabled: v })}
            ariaLabel="Event enabled"
          />
        </div>
      </div>

      {/* Shared year + status row */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Year" hint={`${calendarYear} · age ${ageAtEvent}`}>
          <Input
            type="number"
            min={0}
            value={draft.yearOffset}
            onChange={e => patch({ yearOffset: Math.max(0, Number(e.target.value) || 0) })}
            className="text-right font-mono tabular-nums"
          />
        </Field>
        <div className="flex items-end">
          <span className="text-[11px] text-ink-500 dark:text-ink-300">
            {draft.enabled ? 'Included in projection' : 'Excluded (what-if off)'}
          </span>
        </div>
      </div>

      {/* Type-specific fields */}
      <div className="rounded-xl border border-ink-100 bg-ink-50/40 p-3 dark:border-ink-700 dark:bg-ink-800/40">
        <KindFields draft={draft} patch={patch} />
      </div>

      <Field label="Notes">
        <Textarea
          value={draft.notes ?? ''}
          placeholder="Optional"
          rows={2}
          onChange={e => patch({ notes: e.target.value || undefined })}
        />
      </Field>

      <div className="flex items-center justify-between gap-2 border-t border-ink-100 pt-3 dark:border-ink-700">
        <Button variant="ghost" size="sm" leading={<Icon name="trash" size={14} />} onClick={onDelete}>
          Delete
        </Button>
        <div className="flex items-center gap-2">
          {autoSave ? (
            <span className="text-[11px] font-semibold">
              {dirty ? (
                <span className="text-amber-600 dark:text-amber-400">● Saving…</span>
              ) : (
                <span className="text-pos-600 dark:text-pos-500">✓ Saved</span>
              )}
            </span>
          ) : null}
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function tintFor(tone: string): string {
  switch (tone) {
    case 'pos': return '#10b981';
    case 'neg': return '#ef4444';
    case 'accent': return '#4f6bff';
    case 'warning': return '#f59e0b';
    default: return '#64748b';
  }
}

// ── Per-kind field sets ─────────────────────────────────────────────────────

function KindFields({
  draft,
  patch,
}: {
  draft: EventItem;
  patch: (p: Partial<EventItem>) => void;
}) {
  switch (draft.kind) {
    case 'purchase':
      return (
        <PurchaseFields
          amount={draft.amount}
          funding={draft.funding}
          onAmount={v => patch({ amount: v } as Partial<EventItem>)}
          onFunding={f => patch({ funding: f } as Partial<EventItem>)}
          ageAtEvent={0}
        />
      );
    case 'gift':
      return (
        <PurchaseFields
          amount={draft.amount}
          funding={draft.funding}
          onAmount={v => patch({ amount: v } as Partial<EventItem>)}
          onFunding={f => patch({ funding: f } as Partial<EventItem>)}
          ageAtEvent={0}
        />
      );
    case 'home_buy':
      return <HomeBuyFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'home_sell':
      return <HomeSellFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'home_improvement':
      return <HomeImprovementFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'refinance':
      return <RefinanceFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'income_change':
      return <IncomeChangeFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'contribution_change':
      return <ContributionChangeFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'spending_change':
      return <SpendingChangeFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'job_gap':
      return <JobGapFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'windfall':
      return <WindfallFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'roth_conversion':
      return <RothFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'social_security':
      return <SsFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'pension':
      return <PensionFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
    case 'retire':
      return <RetireFields draft={draft} patch={patch as (p: Partial<typeof draft>) => void} />;
  }
}

// ── Purchase / Gift fields (with funding scenario editor) ───────────────────

function PurchaseFields({
  amount,
  newHomeValue,
  funding,
  onAmount,
  onNewHomeValue,
  onFunding,
  ageAtEvent,
}: {
  amount: number;
  newHomeValue?: number;
  funding?: FundingScenario;
  onAmount: (v: number) => void;
  onNewHomeValue?: (v: number) => void;
  onFunding: (f: FundingScenario | undefined) => void;
  ageAtEvent: number;
}) {
  const { assumptions } = useStore();
  const currentFunding: FundingScenario =
    funding ?? {
      id: uuid(),
      name: 'Default',
      sources: [{ id: uuid(), type: 'hysa', amount }],
    };
  const fundingResult = computeFunding(currentFunding, assumptions, {
    currentMortgageBalance: 0,
    ageAtPurchase: ageAtEvent,
    purchaseAmount: amount,
    working: true,
  });
  const coverage = amount > 0 ? Math.min(1, fundingResult.netDelivered / amount) : 0;

  function updateSources(next: FundingSource[]) {
    onFunding({ ...currentFunding, sources: next });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Amount">
          <MoneyInput value={amount} onChange={onAmount} />
        </Field>
        {onNewHomeValue && (
          <Field
            label="New home value (optional)"
            hint="Set if this purchase replaces your home"
          >
            <MoneyInput
              value={newHomeValue ?? 0}
              onChange={v => onNewHomeValue(v)}
            />
          </Field>
        )}
      </div>

      <div>
        <Label>How it's paid for</Label>
        <ul className="mt-2 space-y-2">
          {currentFunding.sources.map(s => (
            <li key={s.id}>
              <FundingSourceRow
                source={s}
                onChange={updated =>
                  updateSources(
                    currentFunding.sources.map(x => (x.id === s.id ? updated : x)),
                  )
                }
                onRemove={() =>
                  updateSources(currentFunding.sources.filter(x => x.id !== s.id))
                }
              />
            </li>
          ))}
        </ul>
        <AddSourceButton
          onAdd={opt =>
            updateSources([...currentFunding.sources, newSource(opt.type, opt.loanKind)])
          }
        />
      </div>

      {/* Live summary — shows up-front cost, tax, and the lifetime cost
           of any new loans (principal + total interest over their term). */}
      <Card tight flush={false} className="bg-white/50">
        {(() => {
          const mortgageLifetime =
            fundingResult.newMortgagePrincipal > 0
              ? fundingResult.newMortgageMonthlyPI * 12 * fundingResult.newMortgageTermYears
              : 0;
          const mortgageInterest = Math.max(
            0,
            mortgageLifetime - fundingResult.newMortgagePrincipal,
          );
          const loansLifetime = fundingResult.newLoans.reduce(
            (s, l) => s + l.monthlyPI * 12 * l.termYears,
            0,
          );
          const loansInterest = fundingResult.newLoans.reduce(
            (s, l) => s + (l.monthlyPI * 12 * l.termYears - l.principal),
            0,
          );
          const totalInterestOverLife = mortgageInterest + loansInterest;
          const cashOutNow = fundingResult.grossOutflow + fundingResult.cashFlow;
          // "Lifetime cost" = what you'll actually pay over time to own
          // this thing: up-front cash + tax + every dollar of P&I on all
          // new loans over their full term. A $50k car financed at 6.5%/5y
          // really costs $58,680 — this surfaces that.
          const lifetimeCost =
            cashOutNow +
            fundingResult.totalTax +
            mortgageLifetime +
            loansLifetime -
            (fundingResult.homeSold ? fundingResult.homeSaleNet : 0);
          return (
            <div className="space-y-1.5">
              {fundingResult.grossOutflow > 0 && (
                <Row label="Cash / investments pulled" value={<Money value={fundingResult.grossOutflow} tone="neg" />} />
              )}
              {fundingResult.totalTax > 0 && (
                <Row label="Tax / penalties" value={<Money value={fundingResult.totalTax} tone="neg" />} />
              )}
              {fundingResult.cashFlow > 0 && (
                <Row label="From cash flow" value={<Money value={fundingResult.cashFlow} tone="neg" />} />
              )}
              {fundingResult.homeSold && (
                <Row label="Home sale net" value={<Money value={fundingResult.homeSaleNet} tone="pos" />} />
              )}
              {fundingResult.newMortgagePrincipal > 0 && (
                <LoanRow
                  title="New mortgage"
                  principal={fundingResult.newMortgagePrincipal}
                  rate={fundingResult.newMortgageAnnualRate}
                  term={fundingResult.newMortgageTermYears}
                  monthlyPI={fundingResult.newMortgageMonthlyPI}
                />
              )}
              {fundingResult.newLoans.map(l => (
                <LoanRow
                  key={l.id}
                  title={l.label}
                  principal={l.principal}
                  rate={l.annualRate}
                  term={l.termYears}
                  monthlyPI={l.monthlyPI}
                />
              ))}
              <div className="border-t border-ink-100 pt-1.5 dark:border-ink-700">
                <Row
                  label="Net delivered"
                  value={<Money value={fundingResult.netDelivered} tone="pos" bold />}
                />
              </div>
              {totalInterestOverLife > 0 && (
                <div className="rounded-lg bg-ink-50 px-2 py-1.5 dark:bg-ink-800/60">
                  <Row
                    label="Total you'll pay (over loan terms)"
                    value={<Money value={lifetimeCost} tone="neg" bold />}
                  />
                  <div className="text-[11px] text-ink-500 dark:text-ink-300">
                    Includes{' '}
                    <span className="font-mono font-semibold text-neg-700 dark:text-neg-500">
                      +${Math.round(totalInterestOverLife).toLocaleString()}
                    </span>{' '}
                    in interest over the life of the loan{fundingResult.newLoans.length + (fundingResult.newMortgagePrincipal > 0 ? 1 : 0) > 1 ? 's' : ''}.
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        <div className="mt-2">
          <div className="flex items-baseline justify-between text-[11px] text-ink-500 dark:text-ink-300">
            <span>Coverage</span>
            <span className="font-mono tabular-nums">{Math.round(coverage * 100)}%</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-ink-100 dark:bg-ink-700">
            <div
              className={`h-full rounded-full ${coverage >= 1 ? 'bg-pos-500' : 'bg-neg-500'}`}
              style={{ width: `${coverage * 100}%` }}
            />
          </div>
        </div>
        {fundingResult.unfunded > 0 && (
          <Banner tone="danger" title={`Short by ${Math.round(fundingResult.unfunded).toLocaleString()}`}>
            Add another funding source.
          </Banner>
        )}
        {fundingResult.warnings.length > 0 && (
          <div className="mt-2 space-y-1">
            {fundingResult.warnings.map((w, i) => (
              <div
                key={i}
                className="text-[11px] text-amber-700 dark:text-amber-400"
              >
                ⚠ {w}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="text-ink-500 dark:text-ink-300">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

/** Summary row for a new loan created by this funding scenario. Makes the
 *  lifetime cost (principal + interest) explicit so the user isn't surprised
 *  that a "$50k financed car" actually costs more than $50k. */
function LoanRow({
  title,
  principal,
  rate,
  term,
  monthlyPI,
}: {
  title: string;
  principal: number;
  rate: number;
  term: number;
  monthlyPI: number;
}) {
  const lifetime = monthlyPI * 12 * term;
  const interest = Math.max(0, lifetime - principal);
  return (
    <div className="space-y-0.5 rounded-lg border border-ink-100 bg-white px-2 py-1.5 dark:border-ink-700 dark:bg-ink-800/60">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-semibold text-ink-900 dark:text-ink-50">{title}</span>
        <span className="font-mono font-semibold tabular-nums">
          ${Math.round(principal).toLocaleString()} @ {(rate * 100).toFixed(2)}% · {term}y
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-2 text-[11px] text-ink-500 dark:text-ink-300">
        <span>
          <span className="font-mono font-semibold tabular-nums text-ink-700 dark:text-ink-100">
            ${Math.round(monthlyPI).toLocaleString()}/mo
          </span>{' '}
          P&I
        </span>
        <span>
          Lifetime{' '}
          <span className="font-mono font-semibold tabular-nums text-neg-700 dark:text-neg-500">
            ${Math.round(lifetime).toLocaleString()}
          </span>
          {' '}(<span className="font-mono tabular-nums">+${Math.round(interest).toLocaleString()}</span> interest)
        </span>
      </div>
    </div>
  );
}

function FundingSourceRow({
  source,
  onChange,
  onRemove,
}: {
  source: FundingSource;
  onChange: (s: FundingSource) => void;
  onRemove: () => void;
}) {
  const meta = optionForSource(source) ?? SOURCE_OPTIONS[0];
  return (
    <div className="rounded-xl border border-ink-100 bg-white p-3 dark:bg-ink-800 dark:border-ink-700">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{meta.label}</div>
          {meta.hint && (
            <div className="text-[11px] text-ink-500 dark:text-ink-300">{meta.hint}</div>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-ink-400 hover:text-neg-600"
        >
          Remove
        </button>
      </div>
      <FundingSourceFields source={source} onChange={onChange} />
      <SourceImpactSummary source={source} />
    </div>
  );
}

/** Per-source live summary: what this one source contributes to the
 *  funding plan. Makes the tax math for stock sales, §121 exclusions, and
 *  early 401(k) withdrawals visible right where the user is configuring
 *  them — rather than only in the aggregate "Tax / penalties" total. */
function SourceImpactSummary({ source }: { source: FundingSource }) {
  const { assumptions } = useStore();
  // Evaluate this single source in isolation so we get its individual impact.
  const single: FundingScenario = { id: 'tmp', name: 'tmp', sources: [source] };
  // Age at event doesn't matter for most sources; use 40 as a safe adult default.
  // The only source that branches on age is retirement_pretax (pre-59½ penalty);
  // users see that as a warning in the aggregate summary as well.
  const r = computeFunding(single, assumptions, {
    currentMortgageBalance: 0,
    ageAtPurchase: 40,
    purchaseAmount: amountOf(source),
    working: true,
  });

  const bits: React.ReactNode[] = [];
  // Cash / investments pulled
  if (r.grossOutflow > 0) {
    bits.push(
      <Bit key="gross" label="Sold">
        <Money value={r.grossOutflow} tone="neg" />
      </Bit>,
    );
  }
  // Taxable_ltcg/stcg: show gain × rate breakdown
  if ((source.type === 'taxable_ltcg' || source.type === 'taxable_stcg') && source.grossAmount > 0) {
    const gain = source.grossAmount * Math.max(0, Math.min(1, source.gainFraction));
    const rate =
      source.type === 'taxable_ltcg'
        ? assumptions.ltcgRate + assumptions.stateTaxRate
        : assumptions.ordinaryRate + assumptions.stateTaxRate;
    bits.push(
      <Bit key="ltcg" label={source.type === 'taxable_ltcg' ? 'LTCG tax' : 'STCG tax'}>
        <span className="font-mono tabular-nums">
          ${Math.round(gain).toLocaleString()} gain × {Math.round(rate * 100)}% ={' '}
          <Money value={r.totalTax} tone="neg" />
        </span>
      </Bit>,
    );
  }
  // Pre-tax 401(k): show ordinary + state + (penalty if pre-59.5)
  if (source.type === 'retirement_pretax' && source.grossAmount > 0) {
    const isEarly = 40 < 59.5; // context default; actual age applied at projection time
    const rate =
      assumptions.ordinaryRate +
      assumptions.stateTaxRate +
      (isEarly ? assumptions.earlyWithdrawalPenalty : 0);
    bits.push(
      <Bit key="401k" label="Tax + penalty">
        <span className="font-mono tabular-nums">
          ${Math.round(source.grossAmount).toLocaleString()} × {Math.round(rate * 100)}% ={' '}
          <Money value={r.totalTax} tone="neg" />
        </span>
      </Bit>,
    );
  }
  // Home sale: §121, tax, net
  if (source.type === 'home_sale' && r.homeSold) {
    bits.push(
      <Bit key="sale" label="Sale price">
        <Money value={r.homeSalePrice} />
      </Bit>,
    );
    if (r.homeSaleTax > 0) {
      bits.push(
        <Bit key="sale-tax" label={source.useSection121 ? 'Tax after §121' : 'Tax (§121 off)'}>
          <Money value={r.homeSaleTax} tone="neg" />
        </Bit>,
      );
    }
    bits.push(
      <Bit key="sale-net" label="Net proceeds">
        <Money value={r.homeSaleNet} tone="pos" />
      </Bit>,
    );
  }
  // Loan / mortgage: principal + P&I + lifetime
  if (r.newMortgagePrincipal > 0) {
    const lifetime = r.newMortgageMonthlyPI * 12 * r.newMortgageTermYears;
    bits.push(
      <Bit key="mort" label="Monthly P&I">
        <Money value={r.newMortgageMonthlyPI} />
      </Bit>,
      <Bit key="mort-total" label="Lifetime cost">
        <Money value={lifetime} tone="neg" />
      </Bit>,
    );
  }
  for (const ln of r.newLoans) {
    const lifetime = ln.monthlyPI * 12 * ln.termYears;
    const interestPaid = Math.max(0, lifetime - ln.principal);
    bits.push(
      <Bit key={`ln-${ln.id}-pi`} label="Monthly P&I">
        <Money value={ln.monthlyPI} />
      </Bit>,
      <Bit key={`ln-${ln.id}-lt`} label="Lifetime cost">
        <span className="font-mono tabular-nums">
          <Money value={lifetime} tone="neg" />
          <span className="text-[10px] text-ink-500 dark:text-ink-300">
            {' '}(+${Math.round(interestPaid).toLocaleString()} interest)
          </span>
        </span>
      </Bit>,
    );
  }
  // Cash flow & HYSA: straightforward
  if (source.type === 'cash_flow' && source.amount > 0) {
    bits.push(
      <Bit key="cf" label="Reduces this year's savings">
        <Money value={source.amount} tone="neg" />
      </Bit>,
    );
  }
  if (source.type === 'hysa' && source.amount > 0) {
    bits.push(
      <Bit key="hysa" label="From HYSA (no tax)">
        <Money value={source.amount} tone="neg" />
      </Bit>,
    );
  }
  if (source.type === 'retirement_roth' && source.amount > 0) {
    bits.push(
      <Bit key="roth" label="From Roth principal (tax-free)">
        <Money value={source.amount} tone="neg" />
      </Bit>,
    );
  }

  // "Delivers" — what this source contributes to the purchase
  if (r.netDelivered > 0) {
    bits.push(
      <Bit key="net" label="Delivers">
        <Money value={r.netDelivered} tone="pos" bold />
      </Bit>,
    );
  }

  if (bits.length === 0) return null;

  return (
    <div className="mt-2 rounded-lg bg-ink-50/70 px-2 py-1.5 dark:bg-ink-900/40">
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
        {bits}
      </div>
      {r.warnings.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {r.warnings.map((w, i) => (
            <div
              key={i}
              className="text-[10px] text-amber-700 dark:text-amber-400"
            >
              ⚠ {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Bit({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-ink-500 dark:text-ink-300">{label}:</span>
      <span>{children}</span>
    </span>
  );
}

function amountOf(source: FundingSource): number {
  switch (source.type) {
    case 'hysa':
    case 'retirement_roth':
    case 'cash_flow':
      return source.amount;
    case 'taxable_ltcg':
    case 'taxable_stcg':
    case 'retirement_pretax':
      return source.grossAmount;
    case 'home_sale':
      return source.salePrice;
    case 'mortgage':
    case 'loan':
      return source.principal;
  }
}

function FundingSourceFields({
  source,
  onChange,
}: {
  source: FundingSource;
  onChange: (s: FundingSource) => void;
}) {
  switch (source.type) {
    case 'hysa':
    case 'retirement_roth':
    case 'cash_flow':
      return (
        <Field label="Amount">
          <MoneyInput value={source.amount} onChange={v => onChange({ ...source, amount: v })} />
        </Field>
      );
    case 'taxable_ltcg':
    case 'taxable_stcg':
      return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Gross amount to sell">
            <MoneyInput
              value={source.grossAmount}
              onChange={v => onChange({ ...source, grossAmount: v })}
            />
          </Field>
          <Field label={`Gain portion — ${Math.round(source.gainFraction * 100)}%`}>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={source.gainFraction}
              onChange={e => onChange({ ...source, gainFraction: Number(e.target.value) })}
              className="w-full accent-accent-600"
            />
          </Field>
        </div>
      );
    case 'retirement_pretax':
      return (
        <Field label="Gross withdrawal (pre-tax)">
          <MoneyInput
            value={source.grossAmount}
            onChange={v => onChange({ ...source, grossAmount: v })}
          />
        </Field>
      );
    case 'home_sale':
      return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Sale price">
            <MoneyInput
              value={source.salePrice}
              onChange={v => onChange({ ...source, salePrice: v })}
            />
          </Field>
          <Field label="Cost basis override (optional)">
            <MoneyInput
              value={source.costBasisOverride ?? 0}
              onChange={v => onChange({ ...source, costBasisOverride: v || undefined })}
            />
          </Field>
          <label className="inline-flex items-center gap-2 text-xs font-medium md:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-accent-600"
              checked={source.useSection121}
              onChange={e => onChange({ ...source, useSection121: e.target.checked })}
            />
            Apply §121 primary-residence exclusion ($500k married / $250k single)
          </label>
        </div>
      );
    case 'mortgage':
      return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Principal">
            <MoneyInput
              value={source.principal}
              onChange={v => onChange({ ...source, principal: v })}
            />
          </Field>
          <Field label="Annual rate">
            <PctInput
              value={source.annualRate}
              onChange={v => onChange({ ...source, annualRate: v })}
              decimals={3}
            />
          </Field>
          <Field label="Term (years)">
            <Input
              type="number"
              min={1}
              max={40}
              value={source.termYears}
              onChange={e => onChange({ ...source, termYears: Number(e.target.value) })}
              className="text-right font-mono tabular-nums"
            />
          </Field>
        </div>
      );
    case 'loan':
      return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Principal">
            <MoneyInput
              value={source.principal}
              onChange={v => onChange({ ...source, principal: v })}
            />
          </Field>
          <Field label="Annual rate">
            <PctInput
              value={source.annualRate}
              onChange={v => onChange({ ...source, annualRate: v })}
              decimals={3}
            />
          </Field>
          <Field label="Term (years)">
            <Input
              type="number"
              min={1}
              max={40}
              value={source.termYears}
              onChange={e => onChange({ ...source, termYears: Number(e.target.value) })}
              className="text-right font-mono tabular-nums"
            />
          </Field>
        </div>
      );
  }
}

interface SourceOption {
  type: FundingSourceType;
  loanKind?: 'auto' | 'personal' | 'heloc' | 'retirement_401k';
  label: string;
  hint?: string;
}

const SOURCE_OPTIONS: SourceOption[] = [
  { type: 'hysa', label: 'HYSA', hint: 'Drains the HYSA bucket. Doesn’t pull from your Checking float.' },
  { type: 'taxable_ltcg', label: 'Sell taxable (long-term)', hint: 'LTCG tax on the gain portion.' },
  { type: 'taxable_stcg', label: 'Sell taxable (short-term)', hint: 'Ordinary income on the gain portion.' },
  { type: 'retirement_pretax', label: 'Pre-tax 401(k)', hint: 'Ordinary income + 10% penalty if pre-59½.' },
  { type: 'retirement_roth', label: 'Roth principal', hint: 'Tax-free principal withdrawal.' },
  { type: 'home_sale', label: 'Sell current home', hint: '§121 exclusion, pays off current mortgage.' },
  { type: 'mortgage', label: 'New home mortgage', hint: 'Primary residence — sets home liability.' },
  { type: 'loan', loanKind: 'auto', label: 'Auto loan', hint: 'Secured by the car; typical 3–7 year term.' },
  { type: 'loan', loanKind: 'personal', label: 'Personal loan', hint: 'Unsecured; typical 2–5 year term.' },
  { type: 'loan', loanKind: 'heloc', label: 'HELOC', hint: 'Secured by home equity. Interest may be deductible if used for home improvement.' },
  { type: 'loan', loanKind: 'retirement_401k', label: '401(k) loan', hint: 'Borrow from yourself. Max $50k / 5-yr term. Opportunity-cost modeled.' },
  { type: 'cash_flow', label: 'From future savings', hint: 'Reduces that year’s contributions.' },
];

/** Stable key for source options (type + loanKind) — used for option lookups
 *  because the bare type alone is no longer unique (loan variants share it). */
function sourceKey(o: { type: FundingSourceType; loanKind?: string }): string {
  return o.loanKind ? `${o.type}:${o.loanKind}` : o.type;
}

function optionForSource(s: FundingSource): SourceOption | undefined {
  if (s.type === 'loan') {
    return SOURCE_OPTIONS.find(o => o.type === 'loan' && o.loanKind === s.loanKind);
  }
  return SOURCE_OPTIONS.find(o => o.type === s.type);
}

function AddSourceButton({
  onAdd,
}: {
  onAdd: (opt: SourceOption) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative mt-2">
      <Button
        variant="outline"
        size="sm"
        block
        leading={<Icon name="plus" size={14} />}
        onClick={() => setOpen(o => !o)}
      >
        Add funding source
      </Button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[28rem] overflow-y-auto rounded-xl border border-ink-200 bg-white p-1 shadow-card dark:bg-ink-800 dark:border-ink-700">
          {SOURCE_OPTIONS.map(o => (
            <button
              key={sourceKey(o)}
              type="button"
              onClick={() => {
                onAdd(o);
                setOpen(false);
              }}
              className="flex w-full flex-col items-start rounded-lg px-3 py-2 text-left hover:bg-ink-50 dark:hover:bg-ink-700"
            >
              <span className="text-sm font-semibold">{o.label}</span>
              {o.hint && (
                <span className="text-[11px] text-ink-500 dark:text-ink-300">{o.hint}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function newSource(type: FundingSourceType, loanKind?: 'auto' | 'personal' | 'heloc' | 'retirement_401k'): FundingSource {
  const id = uuid();
  switch (type) {
    case 'hysa':
      return { id, type, amount: 0 };
    case 'taxable_ltcg':
    case 'taxable_stcg':
      return { id, type, grossAmount: 0, gainFraction: 0.5 };
    case 'retirement_pretax':
      return { id, type, grossAmount: 0 };
    case 'retirement_roth':
      return { id, type, amount: 0 };
    case 'cash_flow':
      return { id, type, amount: 0 };
    case 'home_sale':
      return { id, type, salePrice: 0, useSection121: true };
    case 'mortgage':
      return { id, type, principal: 0, annualRate: 0.065, termYears: 30 };
    case 'loan':
      return {
        id,
        type,
        loanKind: loanKind ?? 'auto',
        principal: 0,
        annualRate: loanDefaultRate(loanKind ?? 'auto'),
        termYears: loanDefaultTerm(loanKind ?? 'auto'),
      };
  }
}

function loanDefaultRate(kind: 'auto' | 'personal' | 'heloc' | 'retirement_401k'): number {
  switch (kind) {
    case 'auto': return 0.065;
    case 'personal': return 0.10;
    case 'heloc': return 0.085;
    case 'retirement_401k': return 0.08;
  }
}

function loanDefaultTerm(kind: 'auto' | 'personal' | 'heloc' | 'retirement_401k'): number {
  switch (kind) {
    case 'auto': return 5;
    case 'personal': return 5;
    case 'heloc': return 10;
    case 'retirement_401k': return 5;
  }
}

// ── Other event field sets ──────────────────────────────────────────────────

function HomeBuyFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'home_buy' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Purchase price">
          <MoneyInput value={draft.purchasePrice} onChange={v => patch({ purchasePrice: v })} />
        </Field>
        <Field label="Down payment">
          <MoneyInput value={draft.downPayment} onChange={v => patch({ downPayment: v })} />
        </Field>
        <Field label="Mortgage principal">
          <MoneyInput value={draft.mortgagePrincipal} onChange={v => patch({ mortgagePrincipal: v })} />
        </Field>
        <Field label="Mortgage rate">
          <PctInput
            value={draft.mortgageAnnualRate}
            onChange={v => patch({ mortgageAnnualRate: v })}
            decimals={3}
          />
        </Field>
        <Field label="Term (years)">
          <Input
            type="number"
            min={1}
            max={40}
            value={draft.mortgageTermYears}
            onChange={e => patch({ mortgageTermYears: Number(e.target.value) })}
            className="text-right font-mono tabular-nums"
          />
        </Field>
        <Field label="New cost basis">
          <MoneyInput value={draft.newCostBasis} onChange={v => patch({ newCostBasis: v })} />
        </Field>
      </div>
      <label className="inline-flex items-center gap-2 text-xs font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 accent-accent-600"
          checked={draft.sellCurrentHome ?? false}
          onChange={e => patch({ sellCurrentHome: e.target.checked })}
        />
        Sell current home at event year (§121 exclusion applied)
      </label>
    </div>
  );
}

function HomeImprovementFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'home_improvement' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  const { assumptions } = useStore();
  const currentFunding: FundingScenario =
    draft.funding ?? {
      id: uuid(),
      name: 'Default',
      sources: [{ id: uuid(), type: 'hysa', amount: draft.cost }],
    };
  const returnPct = draft.cost > 0 ? (draft.valueAdded / draft.cost) * 100 : 0;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Project cost">
          <MoneyInput value={draft.cost} onChange={v => patch({ cost: v })} />
        </Field>
        <Field
          label="Appraised value added"
          hint={
            draft.cost > 0
              ? `${returnPct.toFixed(0)}% of cost returned as home value`
              : undefined
          }
        >
          <MoneyInput
            value={draft.valueAdded}
            onChange={v => patch({ valueAdded: v })}
          />
        </Field>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        <span className="text-ink-500 dark:text-ink-300">Typical ROI presets:</span>
        <PresetBtn
          label="Kitchen (70%)"
          onClick={() => patch({ valueAdded: Math.round(draft.cost * 0.70) })}
        />
        <PresetBtn
          label="Bathroom (65%)"
          onClick={() => patch({ valueAdded: Math.round(draft.cost * 0.65) })}
        />
        <PresetBtn
          label="Deck / outdoor (50%)"
          onClick={() => patch({ valueAdded: Math.round(draft.cost * 0.50) })}
        />
        <PresetBtn
          label="Roof / HVAC (30%)"
          onClick={() => patch({ valueAdded: Math.round(draft.cost * 0.30) })}
        />
        <PresetBtn
          label="Full cost (100%)"
          onClick={() => patch({ valueAdded: draft.cost })}
        />
      </div>
      <label className="inline-flex items-center gap-2 text-xs font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 accent-accent-600"
          checked={draft.addToCostBasis}
          onChange={e => patch({ addToCostBasis: e.target.checked })}
        />
        Add cost to home basis (reduces §121 gain on future sale)
      </label>
      <div>
        <Label>How it's paid for</Label>
        <ul className="mt-2 space-y-2">
          {currentFunding.sources.map(s => (
            <li key={s.id}>
              <FundingSourceRow
                source={s}
                onChange={updated =>
                  patch({
                    funding: {
                      ...currentFunding,
                      sources: currentFunding.sources.map(x =>
                        x.id === s.id ? updated : x,
                      ),
                    },
                  })
                }
                onRemove={() =>
                  patch({
                    funding: {
                      ...currentFunding,
                      sources: currentFunding.sources.filter(x => x.id !== s.id),
                    },
                  })
                }
              />
            </li>
          ))}
        </ul>
        <AddSourceButton
          onAdd={opt =>
            patch({
              funding: {
                ...currentFunding,
                sources: [...currentFunding.sources, newSource(opt.type, opt.loanKind)],
              },
            })
          }
        />
      </div>
      {void assumptions}
    </div>
  );
}

function PresetBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-ink-200 bg-white px-2 py-0.5 font-semibold text-ink-700 hover:border-accent-500 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-100"
    >
      {label}
    </button>
  );
}

function HomeSellFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'home_sell' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Sale price">
          <MoneyInput value={draft.salePrice} onChange={v => patch({ salePrice: v })} />
        </Field>
        <Field label="Cost basis override (optional)">
          <MoneyInput
            value={draft.costBasisOverride ?? 0}
            onChange={v => patch({ costBasisOverride: v || undefined })}
          />
        </Field>
      </div>
      <Field label="Proceeds go to">
        <SegmentedControl
          value={draft.proceedsBucket}
          onChange={v => patch({ proceedsBucket: v })}
          options={[
            { value: 'hysa', label: 'HYSA' },
            { value: 'taxable', label: 'Taxable' },
          ]}
        />
      </Field>
      <label className="inline-flex items-center gap-2 text-xs font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 accent-accent-600"
          checked={draft.useSection121}
          onChange={e => patch({ useSection121: e.target.checked })}
        />
        Apply §121 primary-residence exclusion
      </label>
    </div>
  );
}

function RefinanceFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'refinance' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <Field label="New rate">
        <PctInput
          value={draft.newAnnualRate}
          onChange={v => patch({ newAnnualRate: v })}
          decimals={3}
        />
      </Field>
      <Field label="New term (years)">
        <Input
          type="number"
          min={1}
          max={40}
          value={draft.newTermYears}
          onChange={e => patch({ newTermYears: Number(e.target.value) })}
          className="text-right font-mono tabular-nums"
        />
      </Field>
      <Field label="Cash-out (optional)">
        <MoneyInput
          value={draft.cashOut ?? 0}
          onChange={v => patch({ cashOut: v || undefined })}
        />
      </Field>
    </div>
  );
}

function IncomeChangeFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'income_change' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  const { assumptions } = useStore();
  const current = assumptions.grossAnnualSalary;
  // Backfill a blank salary with the current assumption so the editor opens
  // with the user's actual salary prefilled — much friendlier than 0.
  const value = draft.newAnnualSalary > 0 ? draft.newAnnualSalary : current;
  const delta = value - current;
  const postTax = delta * (1 - assumptions.ordinaryRate - assumptions.stateTaxRate);
  return (
    <div className="space-y-3">
      <Field
        label="New gross annual salary"
        hint={
          <>
            Your current salary: <strong>${Math.round(current).toLocaleString()}</strong> ·
            you can edit this in Assumptions.
          </>
        }
      >
        <MoneyInput value={value} onChange={v => patch({ newAnnualSalary: v })} />
      </Field>
      <div className="flex flex-wrap gap-1.5">
        <SalaryShortcut label="No income" value={0} onPick={patch} />
        <SalaryShortcut label="Barista FIRE" value={60_000} onPick={patch} />
        <SalaryShortcut label="−20% pay cut" value={Math.round(current * 0.8)} onPick={patch} />
        <SalaryShortcut label="Same" value={current} onPick={patch} />
        <SalaryShortcut label="+20% raise" value={Math.round(current * 1.2)} onPick={patch} />
      </div>
      <div
        className={`rounded-xl border px-3 py-2 text-xs ${
          delta === 0
            ? 'border-ink-200 bg-ink-50/60 dark:border-ink-700 dark:bg-ink-800/40'
            : delta > 0
              ? 'border-pos-500/30 bg-pos-50/40 dark:border-pos-500/30 dark:bg-pos-700/10'
              : 'border-neg-500/30 bg-neg-50/40 dark:border-neg-500/30 dark:bg-neg-700/10'
        }`}
      >
        <div className="font-semibold text-ink-900 dark:text-ink-50">
          {delta === 0
            ? 'Same as current salary'
            : delta > 0
              ? `Raise of $${Math.round(delta).toLocaleString()}/yr`
              : `Pay cut of $${Math.round(-delta).toLocaleString()}/yr`}
        </div>
        {delta !== 0 && (
          <div className="text-ink-500 dark:text-ink-300">
            Post-tax impact on taxable savings:{' '}
            <span
              className={`font-mono font-semibold ${
                postTax >= 0 ? 'text-pos-700 dark:text-pos-500' : 'text-neg-700 dark:text-neg-500'
              }`}
            >
              {postTax >= 0 ? '+' : '−'}${Math.round(Math.abs(postTax)).toLocaleString()}/yr
            </span>
          </div>
        )}
      </div>
      <Field label="Duration (years, blank = forever)">
        <Input
          type="number"
          min={1}
          value={draft.durationYears ?? ''}
          onChange={e =>
            patch({ durationYears: e.target.value ? Number(e.target.value) : undefined })
          }
          className="w-32 text-right font-mono tabular-nums"
        />
      </Field>
    </div>
  );
}

function SalaryShortcut({
  label,
  value,
  onPick,
}: {
  label: string;
  value: number;
  onPick: (p: { newAnnualSalary: number }) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick({ newAnnualSalary: value })}
      className="rounded-md border border-ink-200 bg-white px-2 py-1 text-[11px] font-semibold text-ink-700 hover:border-accent-500 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-100"
    >
      {label}
    </button>
  );
}

function ContributionChangeFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'contribution_change' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  const { assumptions } = useStore();
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-ink-500 dark:text-ink-300">
        Leave a field blank to keep it the same. Set to <strong>0</strong> to stop
        contributing to that bucket entirely.
      </p>
      <ContribRow
        label="401(k)"
        current={assumptions.annualContribution401k}
        value={draft.newRetirement}
        onChange={v => patch({ newRetirement: v })}
      />
      <ContribRow
        label="Taxable"
        current={assumptions.annualContributionTaxable}
        value={draft.newTaxable}
        onChange={v => patch({ newTaxable: v })}
      />
      <ContribRow
        label="HYSA"
        current={assumptions.annualContributionHysa}
        value={draft.newHysa}
        onChange={v => patch({ newHysa: v })}
      />
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => patch({ newRetirement: 0, newTaxable: 0, newHysa: 0 })}
          className="rounded-md border border-ink-200 bg-white px-2 py-1 text-[11px] font-semibold hover:border-accent-500 dark:border-ink-700 dark:bg-ink-800"
        >
          Stop all savings
        </button>
        <button
          type="button"
          onClick={() =>
            patch({
              newRetirement: assumptions.annualContribution401k,
              newTaxable: assumptions.annualContributionTaxable,
              newHysa: assumptions.annualContributionHysa,
            })
          }
          className="rounded-md border border-ink-200 bg-white px-2 py-1 text-[11px] font-semibold hover:border-accent-500 dark:border-ink-700 dark:bg-ink-800"
        >
          Keep current
        </button>
        <button
          type="button"
          onClick={() =>
            patch({ newRetirement: undefined, newTaxable: undefined, newHysa: undefined })
          }
          className="rounded-md border border-ink-200 bg-white px-2 py-1 text-[11px] font-semibold hover:border-accent-500 dark:border-ink-700 dark:bg-ink-800"
        >
          Clear all
        </button>
      </div>
      <Field label="Duration (years, blank = forever)">
        <Input
          type="number"
          min={1}
          value={draft.durationYears ?? ''}
          onChange={e =>
            patch({ durationYears: e.target.value ? Number(e.target.value) : undefined })
          }
          className="w-32 text-right font-mono tabular-nums"
        />
      </Field>
    </div>
  );
}

function ContribRow({
  label,
  current,
  value,
  onChange,
}: {
  label: string;
  current: number;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  const active = value !== undefined;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-ink-100 bg-white p-2 dark:border-ink-700 dark:bg-ink-800">
      <Toggle
        checked={active}
        onChange={v => onChange(v ? current : undefined)}
        ariaLabel={`Change ${label} contribution`}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[10px] text-ink-500 dark:text-ink-300">
          Current: ${Math.round(current).toLocaleString()}/yr
        </div>
      </div>
      <div className={active ? 'w-40' : 'pointer-events-none w-40 opacity-40'}>
        <MoneyInput
          value={value ?? current}
          onChange={v => (active ? onChange(v) : undefined)}
        />
      </div>
    </div>
  );
}

function SpendingChangeFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'spending_change' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  const { assumptions } = useStore();
  const current = assumptions.monthlyHouseholdSpend;
  const value = draft.newMonthlySpend > 0 ? draft.newMonthlySpend : current;
  const delta = value - current;
  return (
    <div className="space-y-3">
      <Field
        label="New monthly household spend"
        hint={
          <>
            Current: <strong>${Math.round(current).toLocaleString()}/mo</strong>{' '}
            (${Math.round(current * 12).toLocaleString()}/yr)
          </>
        }
      >
        <MoneyInput value={value} onChange={v => patch({ newMonthlySpend: v })} />
      </Field>
      <div className="flex flex-wrap gap-1.5">
        <SpendShortcut label="−30% lean" value={Math.round(current * 0.7)} onPick={patch} />
        <SpendShortcut label="−10%" value={Math.round(current * 0.9)} onPick={patch} />
        <SpendShortcut label="Same" value={current} onPick={patch} />
        <SpendShortcut label="+20% fat" value={Math.round(current * 1.2)} onPick={patch} />
      </div>
      {delta !== 0 && (
        <div
          className={`rounded-xl border px-3 py-2 text-xs ${
            delta > 0
              ? 'border-neg-500/30 bg-neg-50/40 dark:border-neg-500/30 dark:bg-neg-700/10'
              : 'border-pos-500/30 bg-pos-50/40 dark:border-pos-500/30 dark:bg-pos-700/10'
          }`}
        >
          <div className="font-semibold text-ink-900 dark:text-ink-50">
            {delta > 0
              ? `Spending +$${Math.round(delta).toLocaleString()}/mo`
              : `Spending −$${Math.round(-delta).toLocaleString()}/mo`}
          </div>
          <div className="text-ink-500 dark:text-ink-300">
            Annual change:{' '}
            <span
              className={`font-mono font-semibold ${
                delta < 0 ? 'text-pos-700 dark:text-pos-500' : 'text-neg-700 dark:text-neg-500'
              }`}
            >
              {delta < 0 ? '−' : '+'}${Math.round(Math.abs(delta * 12)).toLocaleString()}/yr
            </span>
          </div>
        </div>
      )}
      <Field label="Duration (years, blank = forever)">
        <Input
          type="number"
          min={1}
          value={draft.durationYears ?? ''}
          onChange={e =>
            patch({ durationYears: e.target.value ? Number(e.target.value) : undefined })
          }
          className="w-32 text-right font-mono tabular-nums"
        />
      </Field>
    </div>
  );
}

function SpendShortcut({
  label,
  value,
  onPick,
}: {
  label: string;
  value: number;
  onPick: (p: { newMonthlySpend: number }) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick({ newMonthlySpend: value })}
      className="rounded-md border border-ink-200 bg-white px-2 py-1 text-[11px] font-semibold text-ink-700 hover:border-accent-500 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-100"
    >
      {label}
    </button>
  );
}

function JobGapFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'job_gap' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Field label="Duration (years)">
        <Input
          type="number"
          min={1}
          value={draft.durationYears}
          onChange={e => patch({ durationYears: Math.max(1, Number(e.target.value) || 1) })}
          className="text-right font-mono tabular-nums"
        />
      </Field>
      <Field label="Monthly spend override (optional)" hint="Default uses your normal spend">
        <MoneyInput
          value={draft.monthlySpendOverride ?? 0}
          onChange={v => patch({ monthlySpendOverride: v || undefined })}
        />
      </Field>
    </div>
  );
}

function WindfallFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'windfall' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Amount">
          <MoneyInput value={draft.amount} onChange={v => patch({ amount: v })} />
        </Field>
        <Field label="Deposit to">
          <Select
            value={draft.toBucket}
            onChange={e => patch({ toBucket: e.target.value as typeof draft.toBucket })}
          >
            <option value="hysa">HYSA</option>
            <option value="taxable">Taxable</option>
            <option value="retirement">Retirement</option>
          </Select>
        </Field>
      </div>
      <label className="inline-flex items-center gap-2 text-xs font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 accent-accent-600"
          checked={draft.taxFree}
          onChange={e => patch({ taxFree: e.target.checked })}
        />
        Already tax-free (e.g., inheritance)
      </label>
    </div>
  );
}

function RothFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'roth_conversion' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  const { assumptions } = useStore();
  const tax = draft.amount * (assumptions.ordinaryRate + assumptions.stateTaxRate);
  return (
    <div className="space-y-3">
      <Field label="Convert amount (from pre-tax to Roth)">
        <MoneyInput value={draft.amount} onChange={v => patch({ amount: v })} />
      </Field>
      <Banner tone="info" title="Estimated tax">
        Tax due on conversion:{' '}
        <span className="font-mono font-semibold tabular-nums">
          ${Math.round(tax).toLocaleString()}
        </span>{' '}
        (paid from HYSA).
      </Banner>
    </div>
  );
}

function SsFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'social_security' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  return (
    <Field
      label="Monthly benefit (today's dollars)"
      hint="Check your SSA statement for an age-adjusted estimate"
    >
      <MoneyInput value={draft.monthlyBenefit} onChange={v => patch({ monthlyBenefit: v })} />
    </Field>
  );
}

function PensionFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'pension' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Field label="Monthly benefit">
        <MoneyInput value={draft.monthlyBenefit} onChange={v => patch({ monthlyBenefit: v })} />
      </Field>
      <Field label="Duration (years, blank = lifetime)">
        <Input
          type="number"
          min={1}
          value={draft.durationYears ?? ''}
          onChange={e =>
            patch({ durationYears: e.target.value ? Number(e.target.value) : undefined })
          }
          className="text-right font-mono tabular-nums"
        />
      </Field>
    </div>
  );
}

function RetireFields({
  draft,
  patch,
}: {
  draft: Extract<EventItem, { kind: 'retire' }>;
  patch: (p: Partial<typeof draft>) => void;
}) {
  const { assumptions } = useStore();
  const yearsUntil = draft.retirementAge - assumptions.currentAge;
  return (
    <div className="space-y-2">
      <Field
        label="Retire at age"
        hint={
          yearsUntil >= 0
            ? `${yearsUntil} years from now`
            : `${-yearsUntil} years ago`
        }
      >
        <Input
          type="number"
          min={assumptions.currentAge + 1}
          max={120}
          value={draft.retirementAge}
          onChange={e => patch({ retirementAge: Number(e.target.value) })}
          className="text-right font-mono tabular-nums"
        />
      </Field>
    </div>
  );
}
